import mime from 'mime/lite';
import { useEffect, useMemo, useState } from 'react';

import { useHtmlElement, useIntersection } from '@farmersdog/corgi-x';

import type { CSSProperties } from 'react';

const FALLBACK_TYPES = [
  'image/jpg',
  'image/jpeg',
  'image/png',
  'image/tiff',
  'image/gif',
];

/** The `Picture` component allows you to render images in next gen formats and incorporate fallback sources. */

export interface PictureProps {
  /** Explicit fallback src */
  src?: string;
  /** An ordered array of sources, most likely in different formats */
  sources: string[];
  /** Required alt image tag */
  alt: string;
  /**
   * A union of loading types. Use `lazy` to defer loading of images outside of
   * the viewport and `eager` to load them immediately. For more information see
   * this resource.
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#attr-loading
   */
  loading?: 'lazy' | 'eager';
  /** rootMargin to use for the intersection observer if you're lazy loading the image */
  rootMargin?: string;
  /** Optional className */
  className?: string;
  /** Optional style attribute */
  style?: CSSProperties;
  /** Image width */
  width?: string;
  /** Image height */
  height?: string;
}

export function Picture({
  sources,
  src,
  alt,
  loading = 'eager',
  rootMargin = '33%',
  ...props
}: PictureProps) {
  const [loaded, setLoaded] = useState(loading !== 'lazy');
  const [imageEl, imageElRef] = useHtmlElement();
  const intersection = useIntersection(imageEl, {
    rootMargin,
  });

  useEffect(() => {
    if (loaded || !intersection) {
      return;
    }

    if (intersection.isIntersecting) {
      setLoaded(true);
    }
  }, [loaded, intersection]);

  const fallbackSrc = useMemo(() => {
    if (src) {
      return src;
    }

    return Array.isArray(sources)
      ? sources.find(srcSet => {
          const type = mime.getType(srcSet);
          return type && FALLBACK_TYPES.includes(type);
        })
      : '';
  }, [src, sources]);

  if (!Array.isArray(sources)) {
    return (
      <img
        ref={imageElRef}
        src={loaded ? fallbackSrc : undefined}
        alt={alt}
        {...props}
      />
    );
  }

  return (
    <picture>
      {sources.map(srcSet => {
        const type = mime.getType(srcSet) ?? undefined;

        return (
          <source
            key={`${alt}-${type}`}
            type={type}
            srcSet={loaded ? srcSet : undefined}
          />
        );
      })}
      {/* important not to give img tag a src or safari double downloads */}
      <img ref={imageElRef} alt={alt} {...props} />
    </picture>
  );
}
